import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ArrayElement } from "../types/ArrayElement"
import {
    FetchReportsResponsePayload,
    FetchReportsSpecification,
    GetLinkingRequestsResponsePayload,
    GetLinkingRequestsSpecification,
    GetPatientDataBaselineResponsePayload,
    GetPatientDataBaselineSpecification,
    GetPatientDataCurrentResponsePayload,
    GetPatientDataCurrentSpecification,
    GetPatientMedicationInformationResponsePayload,
    GetPatientMedicationInformationSpecification,
    GetPatientMedicationRisksResponsePayload,
    GetPatientMedicationRisksSpecification,
    GetPatientOverviewSeveritiesResponsePayload,
    GetPatientOverviewSeveritiesSpecification,
    GetPatientsOfProfessionalResponsePayload,
    GetPatientsOfProfessionalSpecification,
    GetProfessionalNotesResponsePayload,
    GetProfessionalNotesSpecification,
    PassiveValueType,
    PatientDataAvailabilityResponsePayload,
    PatientDataAvailabilitySpecification,
    QuestionnaireTimestamp,
    standardValueTypeAggregationStrategies,
    UpdateCounterMeasureSpecification,
    Value,
    ValueType,
    ValueTypeAggregationStrategies
} from "pokal_api"
import { RootState } from "./store/store"
import { thunkify } from "./thunk/thunkify"

interface ProfessionalDashboardState {
    general: {
        selectedPatient: ArrayElement<GetPatientsOfProfessionalResponsePayload> | null
        fetch: {
            linkingRequests: GetLinkingRequestsResponsePayload | null
            patientsOfProfessional: GetPatientsOfProfessionalResponsePayload | null
        }
    }

    medication: {
        timestamp: QuestionnaireTimestamp
        fetch: {
            medicationEntries: GetPatientMedicationInformationResponsePayload | null
            risks: GetPatientMedicationRisksResponsePayload | null
        }
    }

    data: {
        selectedValueType: ValueType | null
        valueTypeAggregationSelection: Record<
            ValueType,
            ValueTypeAggregationStrategies
        >
        selectedQuestionnaireType:
            | ValueType.PHQUESTIONNAIRE
            | ValueType.SFQUESTIONNAIRE
            | null
        selectedQuestionnaireId: string | null
        selectedMoodEntry: Value | null
        selectedDataPoint: Value | null
        fetch: {
            availability: PatientDataAvailabilityResponsePayload | null
            severities: GetPatientOverviewSeveritiesResponsePayload | null
            currentData: GetPatientDataCurrentResponsePayload | null
            baselineData: GetPatientDataBaselineResponsePayload | null
        }
    }

    report: {
        fetch: {
            reports: FetchReportsResponsePayload | null
        }
    }

    notes: {
        fetch: {
            notes: GetProfessionalNotesResponsePayload | null
        }
    }
}
const name = "professionalDashboardSlice"
const initialState: ProfessionalDashboardState = {
    general: {
        selectedPatient: null,
        fetch: {
            linkingRequests: null,
            patientsOfProfessional: null
        }
    },
    data: {
        selectedValueType: null,
        valueTypeAggregationSelection: standardValueTypeAggregationStrategies,
        fetch: {
            availability: null,
            severities: null,
            currentData: null,
            baselineData: null
        },
        selectedQuestionnaireType: null,
        selectedQuestionnaireId: null,
        selectedMoodEntry: null,
        selectedDataPoint: null
    },
    medication: {
        timestamp: QuestionnaireTimestamp.T0,
        fetch: {
            medicationEntries: null,
            risks: null
        }
    },
    report: {
        fetch: {
            reports: null
        }
    },
    notes: {
        fetch: {
            notes: null
        }
    }
}
const professionalDashboardSlice = createSlice({
    name,
    initialState,
    reducers: {
        selectDashboardPatient: (
            state,
            action: PayloadAction<ArrayElement<GetPatientsOfProfessionalResponsePayload> | null>
        ) => {
            state.general.selectedPatient = action.payload
            if (action.payload === null) {
                state.data.fetch.currentData = null
                state.data.fetch.baselineData = null
            }
        },
        changeMedicationTimestampSelection: (
            state,
            action: PayloadAction<QuestionnaireTimestamp>
        ) => {
            state.medication.timestamp = action.payload
        },
        selectDashboardValueType: (
            state,
            action: PayloadAction<ValueType | null>
        ) => {
            state.data.selectedValueType = action.payload
            if (action.payload === null) {
                state.data.fetch.currentData = null
                state.data.fetch.baselineData = null
            }
        },
        changeAggregationStrategy: (
            state,
            action: PayloadAction<{
                type: PassiveValueType
                strategy: ValueTypeAggregationStrategies
            }>
        ) => {
            state.data.valueTypeAggregationSelection[action.payload.type] =
                action.payload.strategy
        },
        selectQuestionnaire: (
            state,
            action: PayloadAction<{
                type:
                    | ValueType.PHQUESTIONNAIRE
                    | ValueType.SFQUESTIONNAIRE
                    | null
                id: string | null
            }>
        ) => {
            state.data.selectedQuestionnaireType = action.payload.type
            state.data.selectedQuestionnaireId = action.payload.id
        },
        selectMoodEntry: (
            state,
            action: PayloadAction<Value | null>
        ) => {
            state.data.selectedMoodEntry = action.payload
        },
        selectDataPoint: (
            state,
            action: PayloadAction<Value | null>
        ) => {
            state.data.selectedDataPoint = action.payload
        }
    },
    extraReducers: (builder) => {
        thunkify(builder, GetLinkingRequestsSpecification, (state, action) => {
            state.general.fetch.linkingRequests = action.payload
        })
        thunkify(
            builder,
            GetPatientsOfProfessionalSpecification,
            (state, action) => {
                state.general.fetch.patientsOfProfessional = action.payload
            }
        )
        thunkify(
            builder,
            GetPatientDataCurrentSpecification,
            (state, action) => {
                state.data.fetch.currentData = action.payload
            }
        )
        thunkify(
            builder,
            GetPatientDataBaselineSpecification,
            (state, action) => {
                state.data.fetch.baselineData = action.payload
            }
        )
        thunkify(
            builder,
            PatientDataAvailabilitySpecification,
            (state, action) => {
                state.data.fetch.availability = action.payload
            }
        )
        thunkify(
            builder,
            GetPatientOverviewSeveritiesSpecification,
            (state, action) => {
                state.data.fetch.severities = action.payload
            }
        )
        thunkify(builder, FetchReportsSpecification, (state, action) => {
            state.report.fetch.reports = action.payload
        })
        thunkify(
            builder,
            GetPatientMedicationInformationSpecification,
            (state, action) => {
                state.medication.fetch.medicationEntries = action.payload
            }
        )
        thunkify(
            builder,
            GetPatientMedicationRisksSpecification,
            (state, action) => {
                state.medication.fetch.risks = action.payload
            }
        )
        thunkify(
            builder,
            GetProfessionalNotesSpecification,
            (state, action) => {
                state.notes.fetch.notes = action.payload
            }
        )
        thunkify(
            builder,
            UpdateCounterMeasureSpecification,
            (state, action) => {
                if (state.medication.fetch.risks !== null) {
                    state.medication.fetch.risks[
                        action.payload.timestamp
                    ].counterMeasures = [
                        ...state.medication.fetch.risks[
                            action.payload.timestamp
                        ].counterMeasures.filter(
                            (elem) => elem.id !== action.payload.id
                        ),
                        action.payload
                    ]
                }
            }
        )
    }
})

export const {
    selectDashboardPatient,
    selectDashboardValueType,
    changeAggregationStrategy,
    changeMedicationTimestampSelection,
    selectQuestionnaire,
    selectMoodEntry,
    selectDataPoint
} = professionalDashboardSlice.actions

//General Dashboard
export const selectDashboardSelectedPatient = (state: RootState) =>
    state.professionalDashboard.general.selectedPatient
export const selectLinkingRequests = (state: RootState) =>
    state.professionalDashboard.general.fetch.linkingRequests
export const selectPatientsOfProfessional = (state: RootState) =>
    state.professionalDashboard.general.fetch.patientsOfProfessional

//Data Dashboard
export const selectDashboardSelectedValueType = (state: RootState) =>
    state.professionalDashboard.data.selectedValueType
export const selectValueTypeAggregationStrategies = (state: RootState) =>
    state.professionalDashboard.data.valueTypeAggregationSelection

//Data Dashboard - Fetch
export const selectDashboardDataAvailabilities = (state: RootState) =>
    state.professionalDashboard.data.fetch.availability
export const selectDashboardDataSeverities = (state: RootState) =>
    state.professionalDashboard.data.fetch.severities
export const selectDashboardCurrentPatientData = (state: RootState) =>
    state.professionalDashboard.data.fetch.currentData
export const selectDashboardBaselinePatientData = (state: RootState) =>
    state.professionalDashboard.data.fetch.baselineData
export const getDashboardQuestionnaireType = (state: RootState) =>
    state.professionalDashboard.data.selectedQuestionnaireType
export const getDashboardQuestionnaireId = (state: RootState) =>
    state.professionalDashboard.data.selectedQuestionnaireId
export const selectSelectedMoodEntry = (state: RootState) =>
    state.professionalDashboard.data.selectedMoodEntry
export const selectSelectedDataPoint = (state: RootState) =>
    state.professionalDashboard.data.selectedDataPoint

//Medication Dashboard
export const selectMedicationEntries = (state: RootState) =>
    state.professionalDashboard.medication.fetch.medicationEntries
export const selectMedicationRisks = (state: RootState) =>
    state.professionalDashboard.medication.fetch.risks
export const selectMedicationTimestamp = (state: RootState) =>
    state.professionalDashboard.medication.timestamp

//Reports Dashboard
export const selectReports = (state: RootState) =>
    state.professionalDashboard.report.fetch.reports

//Notes
export const selectNotes = (state: RootState) =>
    state.professionalDashboard.notes.fetch.notes

export default professionalDashboardSlice.reducer
